import { useCallback, useMemo, useState } from 'react';

import { cn } from '@lemoment/lib/utils';
import * as Popover from '@radix-ui/react-popover';
import { addMonths, startOfMonth } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import {
  LEAVE_TYPE_LABELS_KO,
  TEAM_EVENT_CALENDAR_COLOR_ID,
  dateToIso,
} from '@lemoment/shared';
import type { LeaveType } from '@lemoment/shared';

import { useMe, useTeamLeaves } from '../../leaves';
import { useCancelTeamEvent, useCrews, useTeamEvents } from '../hooks';
import { colorForId } from './colors';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const EVENT_COLOR = colorForId(TEAM_EVENT_CALENDAR_COLOR_ID);

// 오전/오후 only lives in the free-text reason; pair it with the canonical
// type label so a "1시간 시차" shows as "오후 1시간 시차". Annual keeps its
// real reason (vacation text), not a type label.
const formatAwayLabel = (type: LeaveType, reason?: string): string => {
  if (type === 'annual') {return reason ?? '';}
  const period = reason?.includes('오전')
    ? '오전 '
    : reason?.includes('오후')
      ? '오후 '
      : '';
  return `${period}${LEAVE_TYPE_LABELS_KO[type]}`;
};

interface AwayCrew {
  name: string;
  color: string;
  reason?: string;
}

interface EventEntry {
  id: string;
  title: string;
  /** "종일" or "HH:mm~HH:mm". */
  time: string;
  canDelete: boolean;
}

interface DayCellProps {
  day: number;
  weekday: number;
  isToday: boolean;
  away: AwayCrew[];
  events: EventEntry[];
  monthLabel: string;
  onDeleteEvent: (id: string) => void;
  deletingId: string | null;
}

const Dots = ({ away }: { away: AwayCrew[] }) => (
  <div className="flex h-1.5 items-center justify-center gap-0.5">
    {away.slice(0, 4).map((crew, i) => (
      <span
        key={i}
        className="h-1.5 w-1.5 rounded-full ring-1 ring-card"
        style={{ backgroundColor: crew.color }}
      />
    ))}
    {away.length > 4 && (
      <span className="ml-0.5 font-numeral text-[0.6rem] leading-none text-muted-foreground">
        +{away.length - 4}
      </span>
    )}
  </div>
);

const DayCell = ({
  day,
  weekday,
  isToday,
  away,
  events,
  monthLabel,
  onDeleteEvent,
  deletingId,
}: DayCellProps) => {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const hasEvent = events.length > 0;
  const interactive = away.length > 0 || hasEvent;
  const cellClass = cn(
    'relative flex h-11 w-full flex-col items-center justify-center gap-1 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    isToday ? 'bg-primary/10 ring-1 ring-primary/40' : 'hover:bg-accent',
  );
  const inner = (
    <>
      {hasEvent && (
        <span
          className="absolute right-1 top-1 h-1.5 w-1.5 rotate-45 rounded-[1px]"
          style={{ backgroundColor: EVENT_COLOR }}
          aria-hidden
        />
      )}
      <span
        className={cn(
          'font-numeral text-[0.9rem] leading-none',
          isToday
            ? 'font-semibold text-lemon-ink dark:text-lemon'
            : weekday === 0
              ? 'text-destructive/70'
              : 'text-foreground/80',
        )}
      >
        {day}
      </span>
      {away.length > 0 ? <Dots away={away} /> : <div className="h-1.5" />}
    </>
  );

  const descriptors: string[] = [];
  if (away.length > 0) {
    descriptors.push(`${away.length}명 자리 비움`);
  }
  if (hasEvent) {
    descriptors.push(`팀 이벤트 ${events.length}건`);
  }
  const ariaLabel = `${monthLabel} ${day}일${
    descriptors.length ? `, ${descriptors.join(', ')}` : ''
  }`;

  if (!interactive) {
    return <div className={cellClass}>{inner}</div>;
  }

  return (
    <Popover.Root onOpenChange={(open) => !open && setConfirmId(null)}>
      <Popover.Trigger asChild>
        <button type="button" className={cellClass} aria-label={ariaLabel}>
          {inner}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          className="z-50 w-56 rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-lg outline-none"
        >
          <p className="eyebrow text-muted-foreground">
            {monthLabel} {day}일
          </p>

          {away.length > 0 && (
            <div className="mt-2">
              <p className="text-[0.7rem] font-medium text-muted-foreground">
                자리 비움
              </p>
              <ul className="mt-1.5 space-y-1.5">
                {away.map((crew, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: crew.color }}
                      aria-hidden
                    />
                    <span className="min-w-0 text-foreground">
                      {crew.name}
                      {crew.reason && (
                        <span className="ml-1.5 text-muted-foreground">
                          {crew.reason}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasEvent && (
            <div className={away.length > 0 ? 'mt-3' : 'mt-2'}>
              <p className="text-[0.7rem] font-medium text-muted-foreground">
                팀 이벤트
              </p>
              <ul className="mt-1.5 space-y-2">
                {events.map((ev) => (
                  <li key={ev.id} className="flex items-start gap-2 text-sm">
                    <span
                      className="mt-1.5 h-2 w-2 shrink-0 rotate-45 rounded-[1px]"
                      style={{ backgroundColor: EVENT_COLOR }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-foreground">
                        {ev.title}
                      </span>
                      <span className="block font-numeral text-xs text-muted-foreground">
                        {ev.time}
                      </span>
                    </span>
                    {ev.canDelete &&
                      (confirmId === ev.id ? (
                        <button
                          type="button"
                          disabled={deletingId === ev.id}
                          onClick={() => onDeleteEvent(ev.id)}
                          className="shrink-0 text-xs font-medium text-destructive disabled:opacity-50"
                        >
                          {deletingId === ev.id ? '삭제 중' : '확인'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmId(ev.id)}
                          className="shrink-0 text-xs text-muted-foreground transition-colors hover:text-destructive"
                        >
                          삭제
                        </button>
                      ))}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export const TeamCalendar = () => {
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const todayIso = dateToIso(new Date());
  const monthLabel = `${month + 1}월`;

  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  const leadingBlanks = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();

  const me = useMe();
  const meId = me.data?.crew.id;
  const isAdmin = me.data?.crew.role === 'admin';
  const cancelEvent = useCancelTeamEvent();

  const crewsQuery = useCrews();
  const leavesQuery = useTeamLeaves({
    from: dateToIso(monthStart),
    to: dateToIso(monthEnd),
  });
  const eventsQuery = useTeamEvents({
    from: dateToIso(monthStart),
    to: dateToIso(monthEnd),
  });
  const crews = crewsQuery.data;
  const leaves = leavesQuery.data;
  const isLoading = crewsQuery.isLoading || leavesQuery.isLoading;
  const isError = crewsQuery.isError || leavesQuery.isError;

  const crewById = useMemo(
    () =>
      new Map(
        (crews ?? []).map((crew) => [
          crew.id,
          {
            name: crew.displayName || crew.name,
            color: colorForId(crew.calendarColorId),
          },
        ]),
      ),
    [crews],
  );

  const activeLeaves = useMemo(
    () => (leaves ?? []).filter((leave) => leave.status === 'active'),
    [leaves],
  );

  const awayForDay = useCallback(
    (dayIso: string): AwayCrew[] =>
      activeLeaves
        .filter((leave) => leave.startDate <= dayIso && dayIso <= leave.endDate)
        .map((leave) => {
          const crew = crewById.get(leave.crewId);
          return {
            name: crew?.name ?? '알 수 없음',
            color: crew?.color ?? colorForId(''),
            reason: formatAwayLabel(leave.type, leave.reason),
          };
        }),
    [activeLeaves, crewById],
  );

  // Events stay resilient: a failed events query never blocks the calendar.
  const activeEvents = useMemo(
    () => (eventsQuery.data?.events ?? []).filter((e) => e.status === 'active'),
    [eventsQuery.data],
  );

  const eventsForDay = useCallback(
    (dayIso: string): EventEntry[] =>
      activeEvents
        .filter((e) => e.startDate <= dayIso && dayIso <= e.endDate)
        .map((e) => ({
          id: e.id,
          title: e.title,
          time: e.allDay ? '종일' : `${e.startTime ?? ''}~${e.endTime ?? ''}`,
          canDelete: (!!meId && e.createdBy === meId) || isAdmin,
        })),
    [activeEvents, meId, isAdmin],
  );

  const handleDeleteEvent = useCallback(
    (id: string) =>
      cancelEvent.mutate(id, {
        onSuccess: () => toast.success('팀 이벤트를 삭제했습니다.'),
        onError: () => toast.error('삭제에 실패했습니다.'),
      }),
    [cancelEvent],
  );
  const deletingId = cancelEvent.isPending
    ? (cancelEvent.variables ?? null)
    : null;

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <section className="lift rounded-2xl border border-border bg-card/80 p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-base font-bold tracking-tight text-foreground">
          팀 캘린더
        </h2>
        <div className="flex items-center gap-2">
          <p className="font-display text-lg font-semibold tracking-tight">
            <span className="font-numeral">{year}</span>
            <span className="mx-1 text-muted-foreground">·</span>
            {month + 1}월
          </p>
          <div className="flex gap-1">
            <button
              type="button"
              aria-label="이전 달"
              onClick={() => setViewMonth((v) => addMonths(v, -1))}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="다음 달"
              onClick={() => setViewMonth((v) => addMonths(v, 1))}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="rule-citrus my-4" />

      {isLoading && (
        <div role="status">
          <span className="sr-only">불러오는 중</span>
          <div className="grid grid-cols-7 gap-1" aria-hidden>
            {Array.from({ length: 35 }, (_, i) => (
              <div key={i} className="skeleton h-11 rounded-lg" />
            ))}
          </div>
        </div>
      )}
      {!isLoading && isError && (
        <p className="text-sm text-destructive">캘린더를 불러오지 못했습니다</p>
      )}

      {!isLoading && !isError && (
        <div className="grid grid-cols-7 gap-1">
          {WEEKDAYS.map((label, i) => (
            <span
              key={label}
              className={cn(
                'eyebrow pb-1 text-center text-[0.6rem]',
                i === 0 ? 'text-destructive/60' : 'text-muted-foreground/70',
              )}
            >
              {label}
            </span>
          ))}

          {Array.from({ length: leadingBlanks }, (_, i) => (
            <div key={`blank-${i}`} aria-hidden />
          ))}

          {days.map((day) => {
            const cellDate = new Date(year, month, day);
            const dayIso = dateToIso(cellDate);
            return (
              <DayCell
                key={dayIso}
                day={day}
                weekday={cellDate.getDay()}
                isToday={dayIso === todayIso}
                away={awayForDay(dayIso)}
                events={eventsForDay(dayIso)}
                monthLabel={monthLabel}
                onDeleteEvent={handleDeleteEvent}
                deletingId={deletingId}
              />
            );
          })}
        </div>
      )}
    </section>
  );
};
